

import { useState, useEffect } from "react";
import { clientsApi, eventsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";

interface Client {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

interface ConvertFormData {
  client_id: number | "";
  event_name: string;
  event_date: string;
  event_type: string;
  guests: string;
}

interface ConvertEnquiryFormProps {
  enquiry: any;
  clients: Client[];
  onSubmit: (data: ConvertFormData) => void;
  onCancel: () => void;
}

export default function ConvertEnquiryForm({ enquiry, clients, onSubmit, onCancel }: ConvertEnquiryFormProps) {
  const formatDateForInput = (dateStr?: string) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return "";
    }
  };

  const getClientName = (e: any) => e.client_name || e.client?.name || e.name || "Unknown Client";
  const getEventType = (e: any) => e.type || e.event_type || "";
  const getGuestCount = (e: any) => (e.guest_count !== undefined ? e.guest_count : e.guests)?.toString() || "";

  const [formData, setFormData] = useState<ConvertFormData>({
    client_id: enquiry.client_id || "",
    event_name: `${getClientName(enquiry)} - ${getEventType(enquiry) || "Event"}`,
    event_date: formatDateForInput(enquiry.event_date) || "",
    event_type: getEventType(enquiry),
    guests: getGuestCount(enquiry),
  });

  // Force re-hydration to guarantee values are never missing due to state delays
  useEffect(() => {
    setFormData({
      client_id: enquiry.client_id || "",
      event_name: `${getClientName(enquiry)} - ${getEventType(enquiry) || "Event"}`,
      event_date: formatDateForInput(enquiry.event_date) || "",
      event_type: getEventType(enquiry),
      guests: getGuestCount(enquiry),
    });
  }, [enquiry]);

  const [errors, setErrors] = useState<Partial<Record<keyof ConvertFormData, string>>>({});
  const [bookedEventsOnDate, setBookedEventsOnDate] = useState<any[]>([]);
  const [isDateChecking, setIsDateChecking] = useState(false);

  // Auto-select client if matching email/phone found
  useEffect(() => {
    if (clients.length > 0) {
      const matchingClient = clients.find(
        c => (c.email && c.email === enquiry.client_email) || 
             (c.phone && c.phone === enquiry.client_phone) ||
             (c.email && c.email === enquiry.email) ||
             (c.phone && c.phone === enquiry.phone)
      );
      if (matchingClient) {
        setFormData(prev => ({ ...prev, client_id: matchingClient.id }));
      }
    }
  }, [clients, enquiry]);

  // Check for date conflicts
  useEffect(() => {
    const checkAvailability = async () => {
      if (!formData.event_date) {
        setBookedEventsOnDate([]);
        return;
      }
      setIsDateChecking(true);
      try {
        const response = await eventsApi.list();
        const eventsList = response.data?.data || response.data || [];
        const conflicts = eventsList.filter((e: any) => 
          formatDateForInput(e.event_date) === formData.event_date
        );
        setBookedEventsOnDate(conflicts);
      } catch (err) {
        console.error("Failed to check availability", err);
      } finally {
        setIsDateChecking(false);
      }
    };
    checkAvailability();
  }, [formData.event_date]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ConvertFormData, string>> = {};
    
    if (!formData.event_name.trim()) newErrors.event_name = "Event name/title is required";
    if (!formData.event_date) newErrors.event_date = "Event date is required";
    if (!formData.event_type.trim()) newErrors.event_type = "Event type is required";
    if (!formData.guests || parseInt(formData.guests) <= 0) newErrors.guests = "Valid guest count is required";

    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof ConvertFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof ConvertFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-1">Converting Enquiry</div>
        <div className="text-lg font-bold text-stone-800">{getClientName(enquiry)}</div>
        <div className="text-sm text-stone-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {enquiry.event_date && (
            <span className="flex items-center gap-1.5">
              <span>📅</span> {formatDate(enquiry.event_date)}
            </span>
          )}
          {getEventType(enquiry) && (
            <span className="flex items-center gap-1.5">
              <span className="text-stone-300">•</span>
              {getEventType(enquiry)}
            </span>
          )}
          {getGuestCount(enquiry) && (
            <span className="flex items-center gap-1.5">
              <span className="text-stone-300">•</span>
              {getGuestCount(enquiry)} guests
            </span>
          )}
        </div>
      </div>

      {/* Client Selection */}
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Select Client *</label>
        <select
          value={formData.client_id}
          onChange={(e) => handleChange("client_id", parseInt(e.target.value) || "")}
          className={inputClass("client_id")}
        >
          <option value="">Create new client: {getClientName(enquiry)}</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.name} {client.phone ? `- ${client.phone}` : ""}
            </option>
          ))}
        </select>
        {errors.client_id && <p className="text-xs text-rose-500 mt-1">{errors.client_id}</p>}
      </div>

      {/* Event Name */}
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Quote Title / Event Name *</label>
        <input
          type="text"
          value={formData.event_name}
          onChange={(e) => handleChange("event_name", e.target.value)}
          placeholder="e.g., Akshay's Wedding"
          className={inputClass("event_name")}
        />
        {errors.event_name && <p className="text-xs text-rose-500 mt-1">{errors.event_name}</p>}
      </div>

      {/* Event Type */}
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Type *</label>
        <input
          type="text"
          value={formData.event_type}
          onChange={(e) => handleChange("event_type", e.target.value)}
          placeholder="e.g., Wedding, Corporate, Birthday"
          className={inputClass("event_type")}
        />
        {errors.event_type && <p className="text-xs text-rose-500 mt-1">{errors.event_type}</p>}
      </div>

      {/* Event Date with Availability Hint */}
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Date *</label>
        <div className="relative">
          <input
            type="date"
            value={formData.event_date}
            onChange={(e) => handleChange("event_date", e.target.value)}
            className={inputClass("event_date")}
          />
        </div>
        {errors.event_date && <p className="text-xs text-rose-500 mt-1">{errors.event_date}</p>}

        {/* Date Availability Indicator */}
        {isDateChecking && <p className="text-xs text-stone-500 mt-1.5 animate-pulse">Checking availability...</p>}
        {!isDateChecking && formData.event_date && bookedEventsOnDate.length > 0 && (
          <div className="mt-2 p-2 bg-rose-50 border border-rose-100 rounded-lg">
            <p className="text-xs text-rose-600 font-medium flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>
              Warning: There are {bookedEventsOnDate.length} mapped event(s) already booked on this date!
            </p>
          </div>
        )}
        {!isDateChecking && formData.event_date && bookedEventsOnDate.length === 0 && (
          <p className="text-xs text-emerald-600 mt-1.5 font-medium flex items-center gap-1.5">
             <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
             Date is available
          </p>
        )}
      </div>

      {/* Guests */}
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Number of Guests *</label>
        <input
          type="number"
          min="1"
          value={formData.guests}
          onChange={(e) => handleChange("guests", e.target.value)}
          placeholder="Enter guest count"
          className={inputClass("guests")}
        />
        {errors.guests && <p className="text-xs text-rose-500 mt-1">{errors.guests}</p>}
      </div>

      {/* Info Note */}
      <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700">
        <p>A new quotation will be created with these details. You can add services and pricing on the next page.</p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          Create & Edit Quotation
        </button>
      </div>
    </form>
  );
}
